
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Helmet } from "react-helmet";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Card } from "@/components/ui/card";
import { BagFilters } from "@/components/BagFilters";
import { BagCard } from "@/components/BagCard";
import { useBags, useFilterOptions } from "@/hooks/useBags";
import { Filters } from "@/types/bag";
import { AboutCreator } from "@/components/AboutCreator";

const BrowseBags = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Filters>({
    category: null,
    year: null,
    color: null,
  });
  const pageSize = 40;

  const { data: filterOptions } = useFilterOptions();
  const { data: bagsData, isLoading, error } = useBags(page, pageSize, filters);

  // If there's an error, show it but don't break the page
  if (error) {
    console.error('Error loading bags:', error);
  }

  const totalPages = Math.ceil((bagsData?.totalCount || 0) / pageSize);

  const handleFilterChange = (key: keyof Filters, value: string | null) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ category: null, year: null, color: null });
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = window.innerWidth < 640 ? 3 : 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      
      let start = Math.max(2, page - 1);
      let end = Math.min(totalPages - 1, page + 1);
      
      if (page <= 2) {
        end = Math.min(3, totalPages - 1);
      } else if (page >= totalPages - 1) {
        start = Math.max(totalPages - 2, 2);
      }
      
      if (start > 2) {
        pages.push("...");
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (end < totalPages - 1) {
        pages.push("...");
      }
      
      pages.push(totalPages);
    }
    
    return pages;
  };

  return (
    <>
      <Helmet>
        <title>Browse Vintage Coach Bags - Complete Collection Database</title>
        <meta name="description" content="Explore our extensive collection of vintage Coach bags. Browse by style, year, or color. View detailed photos and specifications of classic Coach designs." />
      </Helmet>
      <main className="min-h-screen w-full bg-background flex flex-col">
        <div className="flex-grow max-w-7xl mx-auto px-4 pt-20 pb-12">
          <Header />

          <div className="mt-8">
            {filterOptions && (
              <BagFilters
                filters={filters}
                filterOptions={filterOptions}
                onFilterChange={handleFilterChange}
                onClearFilters={clearFilters}
              />
            )}
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
              {Array.from({ length: 8 }).map((_, i) => (
                <Card key={i} className="h-48 animate-pulse bg-muted" />
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
                {bagsData?.bags?.map((bag) => (
                  <BagCard
                    key={bag.id}
                    bag={bag}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <Pagination className="mt-8">
                  <PaginationContent className="gap-1 sm:gap-2">
                    <PaginationItem className="hidden sm:block">
                      <PaginationPrevious
                        onClick={() => handlePageChange(Math.max(1, page - 1))}
                        className={page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>

                    {getPageNumbers().map((pageNum, idx) => (
                      <PaginationItem key={idx}>
                        {pageNum === "..." ? (
                          <span className="px-2 py-2 text-sm">...</span>
                        ) : (
                          <PaginationLink
                            onClick={() => handlePageChange(Number(pageNum))}
                            isActive={page === pageNum}
                            className="cursor-pointer h-8 w-8 sm:h-9 sm:w-9 text-sm p-0"
                          >
                            {pageNum}
                          </PaginationLink>
                        )}
                      </PaginationItem>
                    ))}

                    <PaginationItem className="hidden sm:block">
                      <PaginationNext
                        onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                        className={page === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </>
          )}
        </div>
        <AboutCreator />
      </main>
    </>
  );
};

export default BrowseBags;
